import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white" data-testid="privacy-policy-page">
      {/* Header Banner */}
      <div className="bg-slate-900 py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-slate-400 hover:text-white transition-colors mb-6"
            data-testid="back-to-home-link"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-12 h-12 bg-[#0047AB] rounded-lg flex items-center justify-center">
              <ShieldCheck className="h-6 w-6 text-white" />
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white">
              Privacy Policy
            </h1>
          </div>
          <p className="text-slate-400">
            Effective Date: January 1, 2026
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="legal-page prose prose-slate max-w-none">
          
          <p>
            Muscle & Movement Rehab ("we," "our," or "us") values your privacy. This Privacy Policy 
            explains how we collect, use, disclose, and protect your information when you visit our 
            website, submit a form, book an appointment, or communicate with us, including by SMS.
          </p>
          <p>
            By using our website or services, you consent to the practices described in this Privacy Policy.
          </p>

          <section>
            <h2>1. Information We Collect</h2>
            <p>We may collect the following information:</p>
            
            <h3>Personal Information</h3>
            <ul>
              <li>Name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Mailing address</li>
              <li>Appointment details and preferences</li>
              <li>Insurance information, where applicable</li>
              <li>Health related information relevant to treatment or booking</li>
              <li>Records of consent and opt in actions for SMS and email communications</li>
            </ul>

            <h3>Automatically Collected Information</h3>
            <ul>
              <li>IP address</li>
              <li>Browser type</li>
              <li>Device information</li>
              <li>Website usage data</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>
          </section>

          <section>
            <h2>2. How We Use Your Information</h2>
            <p>We use your information to:</p>
            <ul>
              <li>Schedule and manage appointments</li>
              <li>Provide treatment and related services</li>
              <li>Send appointment reminders and service updates</li>
              <li>Respond to inquiries and support requests</li>
              <li>Process billing and insurance related matters</li>
              <li>Improve our website and services</li>
              <li>Maintain records of communication preferences and consent</li>
              <li>Comply with legal and regulatory requirements</li>
            </ul>
          </section>

          <section>
            <h2>3. SMS Messaging</h2>
            <p>
              If you opt in to receive text messages from Muscle & Movement Rehab, you may receive 
              SMS messages related to:
            </p>
            <ul>
              <li>Appointment reminders</li>
              <li>Appointment confirmations</li>
              <li>Scheduling updates</li>
              <li>Service related follow ups</li>
              <li>Customer support communications</li>
            </ul>
            <p>
              <strong>Message frequency may vary. Message and data rates may apply.</strong>
            </p>
            <p>
              You can opt out at any time by replying <strong>STOP</strong> to any message. For help, 
              reply <strong>HELP</strong> or contact us at{" "}
              <a href="mailto:admin@muscleandmovement.com">admin@muscleandmovement.com</a> or{" "}
              <a href="tel:+16474903782">647-490-3782</a>.
            </p>
            <p>
              We only send SMS messages to individuals who have provided consent to receive them. 
              We maintain records of opt in actions and consent where required.
            </p>
          </section>

          <section className="bg-blue-50 p-6 rounded-lg border border-blue-100 my-8">
            <h2 className="text-[#0047AB] mt-0">4. Mobile Information Sharing</h2>
            <p className="mb-0">
              <strong>No mobile information will be shared with third parties/affiliates for 
              marketing/promotional purposes.</strong> Information sharing to subcontractors in 
              support services, such as customer service, is permitted. All other use case 
              categories exclude text messaging originator opt-in data and consent; this 
              information will not be shared with any third parties.
            </p>
          </section>

          <section>
            <h2>5. Information Sharing and Disclosure</h2>
            <p>We do not sell, rent, or trade your personal information.</p>
            <p>We may share your information only as necessary with:</p>
            <ul>
              <li>Service providers that help us operate our business, such as scheduling platforms, payment processors, and SMS providers</li>
              <li>Healthcare or insurance related parties, where necessary for treatment, billing, or claims processing</li>
              <li>Legal or regulatory authorities when required by law</li>
              <li>Successors in the event of a merger, acquisition, or transfer of assets</li>
            </ul>
          </section>

          <section>
            <h2>6. Data Security</h2>
            <p>
              We use reasonable administrative, technical, and physical safeguards to protect your 
              information from unauthorized access, use, or disclosure. However, no method of 
              transmission over the internet or storage system is completely secure, and we cannot 
              guarantee absolute security.
            </p>
          </section>

          <section>
            <h2>7. Cookies and Tracking Technologies</h2>
            <p>
              Our website may use cookies and similar technologies to improve functionality, 
              understand site usage, and enhance user experience. You can manage cookie preferences 
              through your browser settings.
            </p>
          </section>

          <section>
            <h2>8. Your Rights and Choices</h2>
            <p>You may have the right to:</p>
            <ul>
              <li>Access or correct your personal information</li>
              <li>Request deletion of your information, subject to legal requirements</li>
              <li>Withdraw consent for marketing communications</li>
              <li>Opt out of SMS messages by replying STOP</li>
              <li>Contact us with questions about how your information is used</li>
            </ul>
          </section>

          <section>
            <h2>9. Third Party Links</h2>
            <p>
              Our website may contain links to third party websites. We are not responsible for the 
              privacy practices of those websites.
            </p>
          </section>

          <section>
            <h2>10. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. Any updates will be posted on 
              this page with a revised effective date.
            </p>
          </section>

          <section>
            <h2>11. Contact Us</h2>
            <div className="bg-slate-50 p-6 rounded-lg mt-4">
              <p className="font-semibold text-slate-800 mb-2">Muscle & Movement Rehab</p>
              <p className="mb-1">5800 Sheppard Avenue East, Unit 13</p>
              <p className="mb-1">Scarborough, ON M1B 5J7</p>
              <p className="mb-1">
                Email: <a href="mailto:admin@muscleandmovement.com">admin@muscleandmovement.com</a>
              </p>
              <p className="mb-1">
                Phone: <a href="tel:+16474903782">647-490-3782</a>
              </p>
              <p>
                Website: <a href="https://muscleandmovement.com" target="_blank" rel="noopener noreferrer">muscleandmovement.com</a>
              </p>
            </div>
          </section>

        </div>

        {/* Related Links */}
        <div className="mt-12 pt-8 border-t border-slate-200">
          <h3 className="font-heading font-semibold text-slate-800 mb-4">Related Documents</h3>
          <div className="flex flex-wrap gap-4">
            <Link 
              to="/terms-conditions" 
              className="text-[#0047AB] hover:underline"
              data-testid="related-terms-link"
            >
              Terms & Conditions
            </Link>
            <a 
              href="https://muscleandmovement.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#0047AB] hover:underline"
            >
              Main Website
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
